import { IoClose } from 'react-icons/io5';
import React from 'react';
import { removeEventTicketFromCart } from '../../ShoppingCartSliceV2';
import { Product } from '../../interfaces';
import { RootState } from '../../../../store/store';
import { useDispatch, useSelector } from 'react-redux';
import ProductPreviewComponent from '../../widgets/ProductPreviewComponent';
import { eventLink } from '@skygroup/shared/utils/B2CLinks';
import moment from 'moment';
import { currencySignsObj } from '@skygroup/shared/utils/constants';
import { Divider, Tooltip } from 'antd';
import { FaTruck } from 'react-icons/fa';
import { MainProductType } from '../../interfaces';
import { InfoCircleOutlined } from '@ant-design/icons';
import { Buffer } from 'buffer';
import {
  getDateNotFinalTooltipText,
  getDateTimeBoundariesText,
} from '../../../SportsAndEventsPage/utils/eventDateTimeHelpers';

interface Props {
  product: any; // Product;
  identifier: string;
  isCheckoutPage: boolean;
  isMangeOrderPage: boolean;
  showProductPrice?: boolean;
  mainProductType?: MainProductType;
}

const ShoppingCartEvent = ({
  product,
  identifier,
  isCheckoutPage,
  isMangeOrderPage,
  showProductPrice = true,
  mainProductType,
}: Props) => {
  const dispatch = useDispatch();
  const { siteConfig } = useSelector((data: RootState) => data.config.config);
  const {
    shoppingCartTranslations: t,
    general: general_t,
    hotelDetails: hotelDetails_t,
    sportsAndEvents: sportsAndEvents_t,
  } = useSelector((data: RootState) => data.config.translations);
  const { ShoppingCartV2 } = useSelector(
    (state: RootState) => state.shoppingCartV2
  );

  const eventData = product?.product_data;
  const eventInfo = eventData?.eventInfo ?? eventData;
  const dateTimeBoundariesText = getDateTimeBoundariesText(eventData);
  const dateOrTimeNotFinal =
    eventData?.finalDate === false || eventData?.finalTime === false;
  const dateNotFinalTooltipTitle = getDateNotFinalTooltipText(eventData ?? {}, {
    dateAndTimeAreSubjectToChange:
      sportsAndEvents_t.dateAndTimeAreSubjectToChange,
    dateIsSubjectToChange: sportsAndEvents_t.dateIsSubjectToChange,
    timeIsSubjectToChange: sportsAndEvents_t.timeIsSubjectToChange,
  });

  function generateLink(obj: any) {
    const startDate = moment(
      (obj?.date_start || obj?.subProducts?.[0]?.date_start) * 1000
    ).utc();
    const endDate = moment(
      (obj?.date_end || obj?.subProducts?.[0]?.date_end) * 1000
    ).utc();

    const formattedStartDate = startDate.format(siteConfig.dateFormat);
    const formattedEndDate = endDate.format(siteConfig.dateFormat);
    const searchParams = new URLSearchParams({
      from: formattedStartDate,
      to: formattedEndDate,
    });

    const previewTickets = obj?.subProducts
      ?.map((subProduct: Product) => ({
        id: subProduct.id,
        quantity: subProduct.product_amount,
        deliveryMethodId: subProduct?.product_data?.deliveryMethod?.id,
      }))
      .filter((item: { id: string | number }) => item.id);

    if (previewTickets?.length) {
      searchParams.set(
        'previewTickets',
        Buffer.from(JSON.stringify(previewTickets)).toString('base64')
      );
    }

    return `?${searchParams.toString()}`;
  }

  const handleDeleteEvent = (
    productIdentifier: string,
    subProductId: string
  ) => {
    dispatch(
      removeEventTicketFromCart({
        eventIdentifier: productIdentifier,
        eventTicketId: subProductId,
      })
    );
  };

  return (
    <div key={identifier} className="product-top event-product">
      <div className="product-title">
        <ProductPreviewComponent
          productPath={eventLink(identifier, generateLink(product))}
          label={product.name}
          showActionButtons={mainProductType !== MainProductType.DynamicPackage}
        />
      </div>

      {eventInfo && (
        <div className="event-info">
          {eventInfo.venue && (
            <div className="event-venue">
              <b>{sportsAndEvents_t?.venue}: </b>
              <span>{eventInfo.venue.name}</span>
              {eventInfo.venue.address && (
                <span className="venue-address">
                  {' '}
                  - {eventInfo.venue.address}
                </span>
              )}
            </div>
          )}
          {eventInfo.city && eventInfo.country && (
            <div className="event-location">
              <b>{sportsAndEvents_t?.location}: </b>
              <span>
                {eventInfo.city.name}, {eventInfo.country.name}
              </span>
            </div>
          )}
        </div>
      )}

      <div
        className={'sub-products-title'}
      >{`${general_t?.tickets} ${t?.details}`}</div>

      <div className="subProduct-wrapper">
        {!!product?.subProducts?.length &&
          product.subProducts.map((subProduct: Product, ii: number) => {
            const ticketData = subProduct?.product_data?.ticketDetails;
            const deliveryMethod = subProduct?.product_data?.deliveryMethod;
            const currencySign =
              currencySignsObj?.[
                ticketData?.currency?.toLowerCase() as keyof typeof currencySignsObj
              ] ?? ticketData?.currency;

            return (
              <div
                className="subProduct-row"
                key={`${identifier}_${subProduct.id}`}
              >
                {ii > 0 && <Divider dashed />}
                {subProduct?.name && (
                  <div>
                    <b>{general_t?.ticketType}: </b>
                    <span className="title">{subProduct?.name}</span>
                  </div>
                )}

                {(subProduct?.date_start || product?.date_start) && (
                  <div>
                    <b>{general_t?.date}: </b>{' '}
                    <span className="title">
                      {moment(
                        (subProduct?.date_start || product?.date_start) * 1000
                      ).format(siteConfig.dateFormat)}
                    </span>
                    {eventInfo?.timeOfEvent && (
                      <span className="event-time">
                        {eventInfo.timeOfEvent}
                      </span>
                    )}
                    {dateTimeBoundariesText && (
                      <div className="date-time-boundaries">
                        {dateTimeBoundariesText}
                      </div>
                    )}
                  </div>
                )}

                {/* Date Not Final Hint */}
                {dateOrTimeNotFinal && (
                  <Tooltip
                    className="date not-final-tooltip"
                    title={dateNotFinalTooltipTitle}
                  >
                    <InfoCircleOutlined /> &nbsp;
                    <span>{sportsAndEvents_t.dateNotFinal}</span>
                  </Tooltip>
                )}

                <div className="price-per-ticket-row">
                  <b>{general_t?.price}: </b>
                  <span className="title">
                    {subProduct?.product_amount > 1 &&
                      `${subProduct.product_amount} × `}
                    {Number(subProduct.product_price)?.toFixed()}
                    {currencySign}
                  </span>
                </div>

                {deliveryMethod && (
                  <div className="delivery-method-info">
                    <span className="delivery-label">
                      <b>
                        <FaTruck className="delivery-icon" />
                        {`${isMangeOrderPage ? hotelDetails_t?.included : ''} ${general_t?.deliveryMethod}: `}
                      </b>
                    </span>

                    {deliveryMethod.price > 0 && (
                      <span className="delivery-price">
                        {!isMangeOrderPage ? '+' : ''}
                        {deliveryMethod.price}
                        {currencySign}
                      </span>
                    )}
                    <span className="title">{deliveryMethod.name}</span>
                  </div>
                )}

                <div className={'last-row'}>
                  <div className="product-guests">
                    {/* {subProduct?.related_guest_indexes?.length > 0 && (
                      <div className="icon-box">
                        <b>
                          {general_t?.amount}: x{' '}
                          {subProduct.related_guest_indexes.length}
                        </b>
                      </div>
                    )} */}
                  </div>
                  {!ShoppingCartV2.tp_id && subProduct?.total_price && (
                    <div className="price-box">
                      {showProductPrice && (
                        <span>{`${subProduct?.total_price?.toFixed()} ${
                          currencySignsObj?.[
                            ticketData?.currency?.toLowerCase() as keyof typeof currencySignsObj
                          ] ??
                          ticketData?.currency ??
                          ShoppingCartV2.currency_sign
                        }`}</span>
                      )}
                      {!isCheckoutPage &&
                        mainProductType !== MainProductType.DynamicPackage && (
                          <IoClose
                            size={15}
                            className="delete-product"
                            onClick={() =>
                              handleDeleteEvent(identifier, subProduct.id)
                            }
                          />
                        )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
      </div>
    </div>
  );
};

export default ShoppingCartEvent;
